import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { isValid } from 'date-fns';

import { LinksService } from '@app/core/links.service';
import { StorageService } from '@app/core/storage.service';
import { emailValidator } from '@app/shared/email-validator';
import { OmguiButtonSize, OmguiButtonType } from '@omgui/omgui-button/omgui-button.component';

import { VerifyIdentityGraphQLService } from './verify-identity-graphql.service';

@Component({
  selector: 'om-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrls: ['./identity-verification.component.scss'],
})
export class IdentityVerificationComponent implements OnInit {
  identityForm: FormGroup;
  isSubmitting = false;

  protected readonly OmguiButtonSize = OmguiButtonSize;
  protected readonly OmguiButtonType = OmguiButtonType;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private verifyIdentityService: VerifyIdentityGraphQLService,
    readonly links: LinksService,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    const savedEmail = this.storageService.getItem('forgot_password_email') || '';
    this.identityForm = this.formBuilder.group({
      dateOfBirth: ['', [Validators.required, this.validateDob]],
      phoneNumber: ['', [Validators.required, this.validatePhone]],
      preferredEmail: [savedEmail, [Validators.required, emailValidator]],
    });
  }

  validateDob = (control: any) => {
    const value = control.value;
    if (!value?.length) {
      return { missing: true };
    }

    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return { invalidFormat: true };
    }

    const enteredDate = new Date(value);
    if (!isValid(enteredDate)) {
      return { invalidDate: true };
    }

    return null;
  };

  validatePhone = (control: any) => {
    const value = control.value;
    if (!value) {
      return { required: true };
    }

    const cleanValue = value.replace(/\D/g, '');
    if (!/^[0-9]{10}$/.test(cleanValue)) {
      return { pattern: true };
    }

    return null;
  };

  maskPhoneNumber(target: any): void {
    if (target.value === null) {return;}

    const groups = target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    if (!groups[2]) {
      target.value = groups[1];
    } else if (!groups[3]) {
      target.value = `(${groups[1]}) ${groups[2]}`;
    } else {
      target.value = `(${groups[1]}) ${groups[2]}-${groups[3]}`;
    }
    this.identityForm.patchValue({ phoneNumber: target.value });
  }

  maskBirthDate(target: any): void {
    if (target.value === null) {return;}

    const groups = target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,2})(\d{0,4})/);
    if (!groups[2]) {
      target.value = groups[1];
    } else if (!groups[3]) {
      target.value = `${groups[1]}/${groups[2]}`;
    } else {
      target.value = `${groups[1]}/${groups[2]}/${groups[3]}`;
    }
    this.identityForm.patchValue({ dateOfBirth: target.value });
  }

  onSubmit(): void {
    if (this.isSubmitting || this.identityForm.invalid) {
      if (this.identityForm.invalid) {
        Object.keys(this.identityForm.controls).forEach(key => this.identityForm.get(key)?.markAsTouched());
      }
      return;
    }

    const { dateOfBirth, phoneNumber, preferredEmail } = this.identityForm.value;
    this.isSubmitting = true;
    this.storageService.setItem('forgot_password_email', preferredEmail);

    this.verifyIdentityService.mutate({ input: { email: preferredEmail, phoneNumber, dateOfBirth } }).subscribe({
      next: result => this.handleVerificationResponse(result),
      error: () => this.handleVerificationError(),
    });
  }

  private handleVerificationResponse(result: any): void {
    this.isSubmitting = false;
    const response = result.data?.verifyYourIdentity;

    if (response?.status === 'code_created' && response.verificationToken) {
      this.storageService.setItem('verificationToken', response.verificationToken);
      this.router.navigate(['/forgot_password/otp_verification'], {
        state: { phoneNumber: response.maskedPhoneNumber || '' },
      });
      return;
    }

    this.router.navigate(['/forgot_password/password_reset_email_sent']);
  }

  private handleVerificationError(): void {
    this.isSubmitting = false;
    this.router.navigate(['/forgot_password/password_reset_email_sent']);
  }
}
